import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const questions = [
  {
    id: 'q1',
    question: 'Quel est votre objectif principal ?',
    options: [
      'Construire un patrimoine solide sur le long terme',
      'Générer des revenus passifs (dividendes)',
      'Maximiser la croissance de mon capital',
      'Battre le marché avec du stock picking',
      'Préparer ma retraite sereinement'
    ]
  },
  {
    id: 'q2',
    question: 'Comment gérez-vous votre budget actuellement ?',
    options: [
      'J\'ai un budget strict et je le respecte',
      'Je suis mes dépenses mais sans budget fixe',
      'Je n\'ai pas de suivi particulier'
    ]
  },
  {
    id: 'q3',
    question: 'Avez-vous un fonds d\'urgence ?',
    options: [
      'Non, pas encore',
      'Oui, 1-3 mois de dépenses',
      'Oui, 3-6 mois ou plus'
    ]
  },
  {
    id: 'q4',
    question: 'Quel est votre niveau d\'investissement actuel ?',
    options: [
      'Je débute complètement',
      'J\'ai quelques placements (Livret A, assurance-vie)',
      'J\'investis régulièrement en bourse',
      'J\'ai un portefeuille diversifié multi-actifs'
    ]
  },
  {
    id: 'q5',
    question: 'Quel est votre horizon d\'investissement ?',
    options: [
      'Court terme (< 3 ans)',
      'Moyen terme (3-10 ans)',
      'Long terme (> 10 ans)'
    ]
  },
  {
    id: 'q6',
    question: 'Si votre portefeuille perd 20% en un mois, que faites-vous ?',
    options: [
      'Je vends tout pour limiter les pertes',
      'J\'attends que ça remonte',
      'J\'achète plus pour profiter des soldes'
    ]
  },
  {
    id: 'q7',
    question: 'Si votre portefeuille gagne 40% en un an, que faites-vous ?',
    options: [
      'Je sécurise mes gains et je vends',
      'Je profite de l\'élan pour investir plus',
      'Je ne change rien à ma stratégie'
    ]
  },
  {
    id: 'q8',
    question: 'Quel rendement annuel visez-vous ?',
    options: [
      '4-6% (sécuritaire)',
      '7-10% (équilibré)',
      '10%+ (agressif)'
    ]
  },
  {
    id: 'q9',
    question: 'Quel niveau de complexité acceptez-vous ?',
    options: [
      'Simple : ETF World et c\'est tout',
      'Intermédiaire : ETF + quelques actions',
      'Avancé : Multi-actifs, stock picking, produits dérivés'
    ]
  },
  {
    id: 'q10',
    question: 'Comment souhaitez-vous diversifier ?',
    options: [
      'Minimum : un ou deux ETF suffisent',
      'Modéré : actions, ETF, un peu de crypto',
      'Maximum : actions, ETF, crypto, immobilier, obligations'
    ]
  },
  {
    id: 'q11',
    question: 'Combien de temps pouvez-vous consacrer à vos finances ?',
    options: [
      '< 1h par mois (passif)',
      '1-4h par mois (actif modéré)',
      '> 4h par mois (actif)'
    ]
  }
];

const profileDescriptions = {
  architecte: {
    emoji: '🏗️',
    color: '#00e5a0',
    title: 'Architecte',
    subtitle: 'Le bâtisseur méthodique',
    description: 'Vous construisez votre patrimoine brique par brique, avec méthode et patience. Votre force : la régularité et la vision long terme.'
  },
  stratege: {
    emoji: '🎯',
    color: '#4f8eff',
    title: 'Stratège',
    subtitle: 'L\'analyste avisé',
    description: 'Vous combinez ETF et stock picking avec intelligence. Votre force : l\'équilibre entre sécurité et opportunités.'
  },
  pilote: {
    emoji: '🚀',
    color: '#7c3aed',
    title: 'Pilote',
    subtitle: 'Le navigateur aguerri',
    description: 'Vous maîtrisez plusieurs classes d\'actifs et n\'hésitez pas à prendre des risques calculés. Votre force : l\'adaptabilité.'
  }
};

const QuestionnairePage = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [profileType, setProfileType] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const { token, updateUser } = useAuth();
  const navigate = useNavigate();

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  const handleAnswer = (questionId, answerIndex) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answerIndex
    }));
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/profile/questionnaire`,
        { answers },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProfileType(response.data.profile_type);
      setShowResult(true);
      updateUser({ profile_completed: true });
      toast.success('Profil créé avec succès !');
    } catch (error) {
      toast.error('Erreur lors de la soumission');
    } finally {
      setLoading(false);
    }
  };

  const currentQ = questions[currentQuestion];
  const isAnswered = answers[currentQ.id] !== undefined;
  const isLastQuestion = currentQuestion === questions.length - 1;
  const allAnswered = questions.every(q => answers[q.id] !== undefined);

  if (showResult && profileType) {
    const profile = profileDescriptions[profileType];
    return (
      <div className="min-h-screen bg-cap-bg flex items-center justify-center p-6">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg w-full"
        >
          <div className="glass-card p-8 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center text-5xl"
              style={{ backgroundColor: `${profile.color}20`, border: `2px solid ${profile.color}` }}
            >
              {profile.emoji}
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <h1 
                className="font-display text-3xl font-bold mb-2"
                style={{ color: profile.color }}
              >
                {profile.title}
              </h1>
              <p className="text-cap-text-secondary text-lg mb-4">
                {profile.subtitle}
              </p>
              <p className="text-cap-text-secondary mb-8">
                {profile.description}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="space-y-4"
            >
              <Button
                onClick={() => navigate('/profile')}
                className="w-full h-12 bg-cap-emerald text-cap-bg font-semibold hover:bg-cap-emerald-hover"
                data-testid="view-cap-score-btn"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Découvrir mon CAP Score
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full h-12 border-white/10 text-white hover:bg-white/5"
                data-testid="go-dashboard-btn"
              >
                Aller au Dashboard
              </Button>
            </motion.div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cap-bg flex flex-col">
      {/* Header */}
      <header className="p-6 border-b border-white/5">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <h1 className="font-display text-xl font-semibold text-white">
              Profil Investisseur
            </h1>
            <span className="text-cap-text-secondary text-sm">
              Question {currentQuestion + 1}/{questions.length}
            </span>
          </div>
          <Progress value={progress} className="h-2 bg-cap-bg-elevated" />
        </div>
      </header>

      {/* Question */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-2xl w-full">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentQuestion}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <h2 className="font-display text-2xl md:text-3xl font-semibold text-white mb-8 text-center">
                {currentQ.question}
              </h2>

              <div className="space-y-3">
                {currentQ.options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleAnswer(currentQ.id, index)}
                    className={`w-full p-4 rounded-xl text-left transition-all ${
                      answers[currentQ.id] === index
                        ? 'bg-cap-emerald/20 border-2 border-cap-emerald text-white'
                        : 'glass-card text-cap-text-secondary hover:text-white hover:border-white/20'
                    }`}
                    data-testid={`question-option-${index}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        answers[currentQ.id] === index
                          ? 'border-cap-emerald bg-cap-emerald'
                          : 'border-white/20'
                      }`}>
                        {answers[currentQ.id] === index && (
                          <div className="w-2 h-2 rounded-full bg-cap-bg" />
                        )}
                      </div>
                      <span className="flex-1">{option}</span>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      {/* Navigation */}
      <footer className="p-6 border-t border-white/5">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Button
            onClick={handlePrev}
            disabled={currentQuestion === 0}
            variant="ghost"
            className="text-cap-text-secondary hover:text-white"
            data-testid="questionnaire-prev-btn"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Précédent
          </Button>

          {isLastQuestion ? (
            <Button
              onClick={handleSubmit}
              disabled={!allAnswered || loading}
              className="bg-cap-emerald text-cap-bg font-semibold hover:bg-cap-emerald-hover px-8"
              data-testid="questionnaire-submit-btn"
            >
              {loading ? 'Analyse...' : 'Voir mon profil'}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              disabled={!isAnswered}
              className="bg-cap-emerald text-cap-bg font-semibold hover:bg-cap-emerald-hover"
              data-testid="questionnaire-next-btn"
            >
              Suivant
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          )}
        </div>
      </footer>
    </div>
  );
};

export default QuestionnairePage;
